import React, { useEffect, useState } from 'react'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { FaRegTrashAlt } from 'react-icons/fa'
import {
  Btn,
  BtnIcon,
  CustomCalendar,
  CustomDropdown,
  CustomDropdownWithFilter,
  CustomInputTextArea,
  CustomModal,
  InputNumeroDecimal,
  Loading,
  NumeroDecimal,
  SectionTitle,
} from '../../../../components'
import {
  AVISO_MODELO_NACIONAL_NFSE,
  LIMITE_DESCRICAO_NFSE,
  LIMITE_INFORMACOES_COMPLEMENTARES_NFSE,
  MARCADORES_DESCRICAO_NFSE,
  RETENCAO_ISS_NAO_RETIDO,
  TIPOS_RETENCAO_ISS,
  TRIBUTACAO_ISS_TRIBUTAVEL,
  TRIBUTACOES_ISS,
} from '../../../../constants/fiscal'
import ModalBuscarNotasNfse from './ModalBuscarNotasNfse'
import TomadorNfseForm from './TomadorNfseForm'
import useEmitirNfse from './useEmitirNfse'
import {
  baseCalculoIss,
  erroCoberturaEmissor,
  formatarCodigoNbs,
  formatarCodigoTributacaoNacional,
  formatarMoeda,
  isTransporteMunicipal,
  municipioIncidencia,
  valorIssEstimado,
} from './emitirNfse.util'

const estiloDica = { color: 'gray', fontSize: 12 }
const estiloAviso = {
  padding: '8px 12px',
  borderRadius: 3,
  background: '#FFF3CD',
  border: '1px solid #FFEEBA',
  color: '#856404',
  fontSize: '0.85rem',
}
const estiloSomenteLeitura = { fontSize: '0.85rem', marginTop: 6 }

function rotuloMunicipio(municipio) {
  return municipio ? `${municipio.name}/${municipio.uf}` : '—'
}

function situacaoNota(nota, municipioFrete) {
  if (nota.nfse_vigente)
    return nota.nfse_vigente.numero
      ? `Já tem NFS-e nº ${nota.nfse_vigente.numero}`
      : 'NFS-e em processamento'
  if (nota.cte_vigente)
    return nota.cte_vigente.numero
      ? `Já tem CT-e nº ${nota.cte_vigente.numero}`
      : 'CT-e em processamento'
  if (municipioFrete && nota.client_city_id !== municipioFrete.id)
    return 'Cliente fora do município da prestação'
  return null
}

// Município da prestação da NFS-e avulsa: UF + cidade (na rota vem fixo, da cidade do Operador Logístico)
function SelectMunicipio({ municipio, ufs, carregarCidades, onChange, onError }) {
  const [uf, setUf] = useState(null)
  const [cidades, setCidades] = useState([])

  useEffect(() => {
    setUf(municipio ? ufs.find(item => item.uf === municipio.uf) || null : null)
  }, [municipio, ufs])

  useEffect(() => {
    let ativo = true
    if (!uf) {
      setCidades([])
      return undefined
    }
    carregarCidades(uf.id)
      .then(lista => ativo && setCidades(lista))
      .catch(error => ativo && onError(error))
    return () => {
      ativo = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uf])

  const cidade = municipio ? cidades.find(item => item.id === municipio.id) || null : null

  return (
    <>
      <div className="p-md-2 p-col-12 EmitirNfse-uf">
        <CustomDropdown
          optionLabel="uf"
          options={ufs}
          value={uf}
          label="UF da prestação:*"
          filter={true}
          onChange={value => {
            setUf(value)
            onChange(null)
          }}
        />
      </div>
      <div className="p-md-4 p-col-12">
        <CustomDropdownWithFilter
          optionLabel="name"
          options={cidades}
          value={cidade}
          label="Município da prestação:*"
          filter={true}
          disabled={!uf}
          onChange={value => onChange({ id: value.id, name: value.name, uf: uf.uf })}
        />
      </div>
    </>
  )
}

export default function ModalEmitirNfse({ visible, tpId, contexto, onClose, onEmitida, onError }) {
  const nfse = useEmitirNfse({ visible, tpId, contexto, onError })
  const { form, emissores, servicos, ufs, frete, avisoFrete, carregando, enviando, daRota } = nfse
  const [buscarNotas, setBuscarNotas] = useState(false)
  const erroCobertura = erroCoberturaEmissor(form.emissor, form.municipio_prestacao)
  // Frete municipal (código 16): a nota precisa ter o cliente no município da prestação
  const municipioFrete =
    form.servico && isTransporteMunicipal(form.servico.codigo_tributacao_nacional)
      ? form.municipio_prestacao
      : null

  const tributavel = form.tributacao_iss === TRIBUTACAO_ISS_TRIBUTAVEL
  const ambienteNacional = Boolean(form.emissor && form.emissor.ambiente_nfse === 'nacional')
  const incidencia = form.servico
    ? municipioIncidencia(
        form.servico.codigo_tributacao_nacional,
        form.municipio_prestacao,
        form.emissor
          ? { id: form.emissor.city_id, name: form.emissor.city_name, uf: form.emissor.city_uf }
          : null,
      )
    : null
  const valorIss = tributavel ? valorIssEstimado(form.valores, form.aliquota) : null
  const opcoesEmissores = emissores.map(emissor => ({
    ...emissor,
    rotulo: `${emissor.razao_social} - ${emissor.cnpj} (${
      emissor.ambiente_nfse === 'municipal' ? 'municipal' : 'nacional'
    })`,
  }))
  const emissorSelecionado = form.emissor
    ? opcoesEmissores.find(item => item.id === form.emissor.id) || null
    : null

  // Events
  const onAdicionarNotas = async ids => {
    try {
      await nfse.adicionarNotas(ids)
      setBuscarNotas(false)
    } catch (error) {
      onError(error)
    }
  }

  const onBtnEmitirClicked = async () => {
    try {
      const emitida = await nfse.emitir()
      onEmitida(emitida)
    } catch (error) {
      onError(error)
    }
  }

  // Render
  return (
    <>
      <CustomModal
        isVisible={visible}
        header={daRota ? 'Emitir NFS-e da rota' : 'Emitir NFS-e'}
        width={1000}
        onClose={onClose}
        contentStyle={{ maxHeight: '80vh', overflowY: 'auto' }}
      >
        {carregando ? (
          <div className="text-center" style={{ marginTop: 20, marginBottom: 20 }}>
            <Loading />
          </div>
        ) : (
          <div className="p-grid">
            <div className="p-col-12">
              <div style={estiloAviso}>{AVISO_MODELO_NACIONAL_NFSE}</div>
            </div>

            <div className="p-col-12">
              <SectionTitle title="Prestador e serviço" />
            </div>
            <div className="p-md-6 p-col-12">
              <CustomDropdown
                optionLabel="rotulo"
                options={opcoesEmissores}
                value={emissorSelecionado}
                label="Emissor:*"
                disabled={opcoesEmissores.length === 1}
                onChange={nfse.onEmissorChange}
              />
              {!opcoesEmissores.length && (
                <span style={{ color: '#e5484d', fontSize: 12 }}>
                  Nenhum emissor de NFS-e neste Operador Logístico: entre em contato com o suporte
                </span>
              )}
            </div>
            <div className="p-md-6 p-col-12">
              <CustomDropdown
                optionLabel="descricao"
                options={servicos}
                value={
                  form.servico ? servicos.find(item => item.id === form.servico.id) || null : null
                }
                label="Serviço:*"
                filter={servicos.length > 5}
                onChange={nfse.onServicoChange}
              />
              {!servicos.length && (
                <span style={estiloDica}>Cadastre os serviços na 2.21 - Serviços (NFS-e)</span>
              )}
            </div>
            {form.servico && (
              <div className="p-col-12" style={{ paddingTop: 0 }}>
                <span style={estiloDica}>
                  Código de tributação nacional{' '}
                  {formatarCodigoTributacaoNacional(form.servico.codigo_tributacao_nacional)} · NBS{' '}
                  {formatarCodigoNbs(form.servico.codigo_nbs)}
                  {form.servico.codigo_tributacao_municipal
                    ? ` · Código municipal ${form.servico.codigo_tributacao_municipal}`
                    : ''}
                </span>
              </div>
            )}
            <div className="p-md-4 p-col-12">
              <CustomDropdown
                optionLabel="label"
                options={TRIBUTACOES_ISS}
                value={TRIBUTACOES_ISS.find(item => item.value === form.tributacao_iss) || null}
                label="Tributação do ISS:*"
                onChange={item =>
                  nfse.atualizar({
                    tributacao_iss: item.value,
                    ...(item.value === TRIBUTACAO_ISS_TRIBUTAVEL
                      ? {}
                      : { tipo_retencao_iss: RETENCAO_ISS_NAO_RETIDO, aliquota: '' }),
                  })
                }
              />
            </div>
            <div className="p-md-4 p-col-12">
              <CustomDropdown
                optionLabel="label"
                options={TIPOS_RETENCAO_ISS}
                value={
                  TIPOS_RETENCAO_ISS.find(item => item.value === form.tipo_retencao_iss) || null
                }
                label="Retenção do ISS:*"
                disabled={!tributavel}
                onChange={item => nfse.atualizar({ tipo_retencao_iss: item.value })}
              />
            </div>
            <div className="p-md-4 p-col-12">
              {tributavel && (
                <>
                  <InputNumeroDecimal
                    label={ambienteNacional ? 'Alíquota do ISS (%):' : 'Alíquota do ISS (%):*'}
                    marginTop={10}
                    value={form.aliquota}
                    onChange={aliquota => nfse.atualizar({ aliquota })}
                  />
                  {ambienteNacional && (
                    <span style={estiloDica}>
                      No Emissor Nacional a alíquota vem do cadastro do município; aqui é só
                      estimativa
                    </span>
                  )}
                </>
              )}
            </div>

            <div className="p-col-12">
              <SectionTitle title="Competência e local" />
            </div>
            <div className="p-md-3 p-col-12">
              <CustomCalendar
                label="Competência:*"
                value={form.data_competencia}
                maxDate={new Date()}
                onChange={nfse.onCompetenciaChange}
              />
            </div>
            {daRota || (contexto && contexto.duplicar) ? (
              <div className="p-md-4 p-col-12">
                <label
                  style={{ fontSize: '0.8rem', color: '#14B0BF', display: 'block', marginTop: 10 }}
                >
                  Município da prestação:
                </label>
                <div style={estiloSomenteLeitura}>{rotuloMunicipio(form.municipio_prestacao)}</div>
              </div>
            ) : (
              <SelectMunicipio
                municipio={form.municipio_prestacao}
                ufs={ufs}
                carregarCidades={nfse.carregarCidades}
                onChange={municipio => nfse.atualizar({ municipio_prestacao: municipio })}
                onError={onError}
              />
            )}
            <div className="p-md-3 p-col-12">
              <label
                style={{ fontSize: '0.8rem', color: '#14B0BF', display: 'block', marginTop: 10 }}
              >
                Município de incidência do ISS:
              </label>
              <div style={estiloSomenteLeitura}>{rotuloMunicipio(incidencia)}</div>
            </div>
            {erroCobertura && (
              <div className="p-col-12" style={{ paddingTop: 0 }}>
                <span style={{ color: '#e5484d', fontSize: 12 }}>{erroCobertura}</span>
              </div>
            )}

            <TomadorNfseForm
              tomador={form.tomador}
              onChange={tomador => nfse.atualizar({ tomador })}
              ufs={ufs}
              carregarCidades={nfse.carregarCidades}
              tpId={tpId}
              onError={onError}
            />

            {!daRota && (
              <div
                className="p-col-12"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <div style={{ flex: 1 }}>
                  <SectionTitle title="Notas Fiscais" />
                </div>
                <Btn onClick={() => setBuscarNotas(true)} style={{ marginLeft: 10 }}>
                  Adicionar notas
                </Btn>
              </div>
            )}
            {!daRota && !form.notas.length && (
              <div className="p-col-12" style={{ paddingTop: 0 }}>
                <span style={estiloDica}>
                  Opcional: vincule notas já importadas no sistema (frete municipal, por exemplo).
                </span>
              </div>
            )}
            {form.notas.length > 0 && (
              <div className="p-col-12">
                <DataTable
                  value={form.notas}
                  header={daRota ? 'Notas Fiscais da NFS-e' : undefined}
                >
                  <Column field="identifier" header="Nota" style={{ width: 110 }} />
                  <Column field="client_nome" header="Cliente" />
                  <Column
                    header="Cidade"
                    style={{ width: 170 }}
                    body={nota => `${nota.client_city_name}/${nota.client_uf}`}
                  />
                  <Column
                    header="Peso"
                    style={{ width: 100, textAlign: 'center' }}
                    body={nota => <NumeroDecimal>{nota.weightTotal}</NumeroDecimal>}
                  />
                  <Column
                    header="Situação"
                    style={{ width: 190 }}
                    body={nota => {
                      const situacao = situacaoNota(nota, municipioFrete)
                      return situacao ? (
                        <span style={{ color: '#e5484d' }}>{situacao}</span>
                      ) : (
                        'Livre'
                      )
                    }}
                  />
                  <Column
                    header=""
                    style={{ width: 60, textAlign: 'center' }}
                    body={nota => (
                      <BtnIcon onClick={() => nfse.onRemoverNota(nota)}>
                        <FaRegTrashAlt color="red" size={18} />
                      </BtnIcon>
                    )}
                  />
                </DataTable>
              </div>
            )}

            <div className="p-col-12">
              <SectionTitle title="Valores" />
            </div>
            <div className="p-md-3 p-col-12">
              <InputNumeroDecimal
                label="Valor do serviço (R$):*"
                marginTop={10}
                value={form.valores.valor_servico}
                onChange={valor => nfse.onValorChange('valor_servico', valor)}
              />
            </div>
            <div className="p-md-3 p-col-12">
              <InputNumeroDecimal
                label="Desconto incondicionado (R$):"
                marginTop={10}
                value={form.valores.desconto_incondicionado}
                onChange={valor => nfse.onValorChange('desconto_incondicionado', valor)}
              />
            </div>
            <div className="p-md-3 p-col-12">
              <InputNumeroDecimal
                label="Desconto condicionado (R$):"
                marginTop={10}
                value={form.valores.desconto_condicionado}
                onChange={valor => nfse.onValorChange('desconto_condicionado', valor)}
              />
            </div>
            <div className="p-md-3 p-col-12">
              <InputNumeroDecimal
                label="Dedução (R$):"
                marginTop={10}
                value={form.valores.valor_deducao}
                onChange={valor => nfse.onValorChange('valor_deducao', valor)}
              />
            </div>
            <div className="p-col-12" style={{ paddingTop: 0 }}>
              {frete && (
                <div style={estiloDica}>
                  Valor sugerido pelas regras de frete
                  {frete.faixa_km_name ? ` (faixa ${frete.faixa_km_name.trim()})` : ''}:{' '}
                  {formatarMoeda(frete.valor)}
                  {frete.taxas_fixas && frete.taxas_fixas.length
                    ? `. Taxas fixas disponíveis: ${frete.taxas_fixas
                        .map(taxa => `${taxa.nome} ${formatarMoeda(taxa.valor)}`)
                        .join(', ')}`
                    : ''}
                </div>
              )}
              {avisoFrete && <div style={estiloDica}>{avisoFrete}</div>}
              <div style={{ fontSize: '0.85rem', marginTop: 6 }}>
                Base de cálculo: {formatarMoeda(baseCalculoIss(form.valores))}
                {valorIss !== null ? ` · ISS estimado: ${formatarMoeda(valorIss)}` : ''}
              </div>
            </div>

            <div className="p-col-12">
              <SectionTitle title="Descrição" />
            </div>
            <div className="p-col-12">
              <CustomInputTextArea
                label="Descrição do serviço*"
                marginTop={0}
                rows={4}
                value={form.descricao_servico}
                onChange={descricao_servico => nfse.atualizar({ descricao_servico })}
              />
              <span style={estiloDica}>
                {String(form.descricao_servico || '').length}/{LIMITE_DESCRICAO_NFSE} · O texto vem
                do modelo do serviço (2.21), com os marcadores{' '}
                {MARCADORES_DESCRICAO_NFSE.map(item => item.marcador).join(', ')}
              </span>
            </div>
            <div className="p-col-12">
              <CustomInputTextArea
                label="Informações complementares"
                marginTop={10}
                rows={2}
                value={form.informacoes_complementares}
                onChange={informacoes_complementares =>
                  nfse.atualizar({ informacoes_complementares })
                }
              />
              <span style={estiloDica}>
                {String(form.informacoes_complementares || '').length}/
                {LIMITE_INFORMACOES_COMPLEMENTARES_NFSE}
              </span>
            </div>

            <div className="p-col-12" style={{ display: 'flex', justifyContent: 'end' }}>
              <Btn onClick={onBtnEmitirClicked} isLoading={enviando} disabled={!emissores.length}>
                Emitir NFS-e
              </Btn>
            </div>
          </div>
        )}
      </CustomModal>
      <ModalBuscarNotasNfse
        visible={buscarNotas}
        tpId={tpId}
        ufs={ufs}
        carregarCidades={nfse.carregarCidades}
        municipioPadrao={form.municipio_prestacao}
        empresaPadraoId={form.notas.length ? form.notas[0].company_id : null}
        idsIncluidos={form.notas.map(nota => nota.id)}
        onClose={() => setBuscarNotas(false)}
        onAdicionar={onAdicionarNotas}
        onError={onError}
      />
    </>
  )
}
